import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { FileText, Upload, RefreshCw, Loader2, Globe } from 'lucide-react';

const LastRun = ({ run }) => {
  if (!run) return <p className="text-xs text-slate-500">Búsqueda automática: todas las noches (03:00 hora de Chile).</p>;
  const when = new Date(run.finished_at || run.started_at).toLocaleString('es-CL');
  return (
    <p data-testid="rulings-last-run" className="text-xs text-slate-400">
      {run.status === 'running' ? 'Búsqueda en curso...' : `Última búsqueda: ${when} · ${run.total_saved ?? 0} fallo(s) nuevo(s)`} · Automática cada noche
    </p>
  );
};

export const RulingsFeed = ({ country, onNewRulings }) => {
  const { api } = useAuth();
  const [data, setData] = useState({ rulings: [], last_run: null });
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/judicial-intelligence/rulings/recent?country=${country}&limit=10`).then((r) => { setData(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { load(); }, [country]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data.last_run?.status !== 'running') return;
    const t = setTimeout(() => { load(); onNewRulings?.(); }, 8000);
    return () => clearTimeout(t);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('country', country);
    setUploading(true);
    try {
      const r = await api.post('/judicial-intelligence/rulings/upload', form);
      toast.success(r.data.message);
      load(); onNewRulings?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir el fallo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const ingest = async () => {
    try {
      const r = await api.post('/judicial-intelligence/rulings/ingest');
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar la búsqueda');
    }
  };

  const running = data.last_run?.status === 'running';
  return (
    <div data-testid="rulings-feed-section" className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><FileText className="w-6 h-6 text-emerald-400" />Fallos Reales</h2>
          {loaded && <LastRun run={data.last_run} />}
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={upload} data-testid="ruling-file-input" />
          <button data-testid="upload-ruling-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold py-2 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{uploading ? 'Analizando...' : 'Subir sentencia'}
          </button>
          <button data-testid="ingest-now-btn" onClick={ingest} disabled={running} className="bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold py-2 px-4 rounded-lg flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />{running ? 'Buscando...' : 'Buscar ahora'}
          </button>
        </div>
      </div>
      {!loaded ? (
        <div data-testid="rulings-loading" className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" />Cargando fallos...</div>
      ) : data.rulings.length === 0 ? (
        <p className="text-sm text-slate-500">Aún no hay fallos para este país. Sube un PDF/DOCX/TXT o pulsa "Buscar ahora".</p>
      ) : (
        <div data-testid="rulings-list" className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.rulings.map((r) => (
            <div key={r.id} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-white font-medium truncate">{r.case_number}</p>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1 shrink-0">
                  {r.source === 'automatico' ? <Globe className="w-3 h-3" /> : <Upload className="w-3 h-3" />}{r.source || 'manual'}
                </span>
              </div>
              <p className="text-xs text-slate-400">{r.court_name} · {r.case_type} · {r.outcome}</p>
              {r.ai_summary && <p className="text-xs text-slate-300 mt-1 line-clamp-2">{r.ai_summary}</p>}
              {r.judge_names?.length > 0 && <p className="text-xs text-slate-500 mt-1 truncate">Jueces: {r.judge_names.join(', ')}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
